import { Ajuda } from '../../design/Ajuda';
import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import {
  vendasUsadas,
  ROTULO_DA_ETAPA,
  ROTULO_DO_MOTIVO,
  bN,
  bloco2,
  diaCurto,
  diaLongo,
  hojeBrasilia,
  iniciais,
  lerBloco,
  lerEquipe,
  lerOperacao,
  pct,
  primeiroNome,
  transferir,
} from '../../giro';
import type {
  BlocoDaOperacao,
  DetalheDoBloco,
  EtapaDoBloco,
  MotivoForaDaMissao,
  OperacaoDoDia,
  PessoaDaEquipe,
  SetorDaOperacao,
} from '../../giro';
import { blocoDaTroca, podeTransferirBloco } from '../../giro-gestor';
import { hora } from '../../vendas';
import { FotosDoGiro } from './FotosDoGiro';
import { SubnavDoGiro } from './SubnavDoGiro';

/**
 * TELA 16 — GIRO DE PRATELEIRA, ADMIN. "O bloco é fixo. A faixa é do dia. A pessoa roda."
 *
 *   16A Visão geral     funil da geração do dia, progresso por etapa, setores, alertas
 *   16B Operação de hoje o mapa de blocos do setor: faixa, SKUs, responsável, progresso e etapa
 *   16C Detalhe do bloco página com rota própria: etapas, autoria por trecho e produtos
 *
 * Atribuído não é concluído. Estoque indisponível não é zero. Simulação não é operação.
 */

const ORDEM_DO_FORA: readonly MotivoForaDaMissao[] = [
  'ESTOQUE_ZERO',
  'ESTOQUE_INDISPONIVEL',
  'ESTOQUE_STALE',
  'SEM_SETOR',
  'SEM_CADASTRO',
  'SETOR_SEM_EQUIPE',
];

export function useOperacao(dia: string) {
  const [d, setD] = useState<OperacaoDoDia | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    void lerOperacao(dia).then((r) => {
      if (r.ok) setD(r.dados);
      else setErro(true);
    });
  }, [dia]);
  useEffect(carregar, [carregar]);
  return { d, erro, carregar };
}

export function Selo({ etapa }: { etapa: EtapaDoBloco }) {
  return (
    <span className={`gr-selo gr-selo-${etapa}`}>
      {etapa === 'CONCLUIDO' && <Icone nome="check" tamanho={12} traco={2} />}
      {ROTULO_DA_ETAPA[etapa]}
    </span>
  );
}
export function Barra({
  valor,
  total,
  tom,
}: {
  valor: number;
  total: number;
  tom?: 'ok' | 'rep' | 'cinza' | undefined;
}) {
  return (
    <div
      className="gr-barra"
      role="progressbar"
      aria-valuenow={valor}
      aria-valuemax={total}
      aria-valuemin={0}
    >
      <i
        className={tom ? `gr-barra-${tom}` : ''}
        style={{ width: `${String(Math.min(100, pct(valor, total)))}%` }}
      />
    </div>
  );
}

function tituloDoDia(op: OperacaoDoDia) {
  const pub = op.setores.find((s) => s.geracao?.publicadoEm);
  return `Operação de ${diaLongo(op.dia).split(',')[0] ?? ''}, ${diaCurto(op.dia)}/${op.dia.slice(0, 4)}${
    pub?.geracao?.publicadoEm
      ? ` · publicada às ${hora(pub.geracao.publicadoEm)}${pub.geracao.publicadoPor ? ` por ${pub.geracao.publicadoPor}` : ''}`
      : ''
  } · vendas de ${vendasUsadas(op)}`;
}

/* ================================================================== *
 * 16A — Visão geral
 * ================================================================== */

export function VisaoGeralDoGiro() {
  const dia = hojeBrasilia();
  const { d, erro, carregar } = useOperacao(dia);
  const semOperacao = d && d.setores.every((s) => !s.geracao);
  return (
    <section className="cv gr">
      <SubnavDoGiro atual="giro" excecoes={d?.foraTotal} />
      <div className="cv-corpo">
        <div className="cv-topo">
          <div className="cv-titulo cv-titulo-2l">
            <h1>Giro de Prateleira</h1>
            <span>{d ? tituloDoDia(d) : ' '}</span>
          </div>
          {d && (
            <div className="cv-topo-dir">
              <span className="gr-chip-fonte">
                <Icone nome="box" tamanho={14} traco={1.6} /> Estoque{' '}
                <b>{d.estoqueCapturadoAte ? hora(d.estoqueCapturadoAte) : 'sem leitura'}</b>
                <span className="gr-tag">API DO HOS</span>
                <Ajuda rotulo="a hora do estoque">
                  Hora da última leitura do estoque no HOS usada pelo Giro. Produto sem leitura
                  recente fica fora do Giro — nunca vira zero.
                </Ajuda>
              </span>
              <a className="cv-botao-primario gr-botao-link" href="#/giro-geracao">
                <Icone nome="plus" tamanho={14} traco={1.8} /> Nova geração
              </a>
            </div>
          )}
        </div>
        {erro ? (
          <ErroDeArea oQue="a operação do Giro" aoTentar={carregar} />
        ) : !d ? (
          <div className="cv-carregando" aria-busy="true" />
        ) : semOperacao ? (
          <SemOperacao dia={dia} vendasDe={vendasUsadas(d)} semSetores={!d.setores.length} />
        ) : (
          <>
            <div className="gr-grade-16a">
              <div className="cv-cartao gr-formacao">
                <div className="cv-cartao-topo">
                  <h2>
                    Como a operação de hoje foi formada
                    <Ajuda rotulo="como a operação foi formada">
                      Parte dos produtos vendidos ontem (na segunda, sábado e domingo). Cada produto
                      conta uma vez, não importa quantas unidades vendeu. Os que não dá para
                      conferir saem antes de montar os blocos.
                    </Ajuda>
                  </h2>
                  <span className="cv-cinza cv-peq">
                    SKUs distintos vendidos em {vendasUsadas(d)} · quantidade não duplica SKU
                  </span>
                </div>
                <div className="gr-funil">
                  <div>
                    <span className="gr-rotulo">
                      VENDIDOS D-1
                      <Ajuda rotulo="vendidos D-1">
                        Quantos produtos diferentes venderam no dia usado (D-1 = ontem).
                      </Ajuda>
                    </span>
                    <strong>{d.vendidosD1 ?? '—'}</strong>
                    <em>SKUs distintos</em>
                  </div>
                  <div>
                    <span className="gr-rotulo">
                      ELEGÍVEIS
                      <Ajuda rotulo="elegíveis">
                        Os que entraram no Giro de hoje e foram divididos nos blocos da equipe.
                      </Ajuda>
                    </span>
                    <strong className="gr-verde">{d.elegiveis}</strong>
                    <em>entraram nos blocos</em>
                  </div>
                  <div className="gr-fora">
                    <span className="gr-rotulo">
                      FORA DA MISSÃO · {d.foraTotal}
                      <Ajuda rotulo="fora da missão">
                        Venderam ontem mas não entraram: estoque zero no HOS (vira ruptura), estoque
                        sem leitura, produto sem setor do Giro ou sem cadastro. A lista completa
                        está em Exceções.
                      </Ajuda>
                    </span>
                    <ul>
                      {ORDEM_DO_FORA.filter(
                        (m) =>
                          d.fora[m] > 0 ||
                          [
                            'ESTOQUE_ZERO',
                            'ESTOQUE_INDISPONIVEL',
                            'SEM_SETOR',
                            'SEM_CADASTRO',
                          ].includes(m),
                      ).map((m) => (
                        <li key={m}>
                          <i className={`gr-ponto gr-ponto-${m}`} />
                          {ROTULO_DO_MOTIVO[m]}
                          <b>{d.fora[m]}</b>
                        </li>
                      ))}
                    </ul>
                    <a href="#/giro-excecoes">Ver exceções da geração</a>
                  </div>
                </div>
              </div>
              <div className="cv-cartao gr-progresso">
                <h2 className="cv-h2 cv-h2-topo">
                  Progresso por etapa
                  <Ajuda rotulo="progresso por etapa">
                    O Giro tem duas etapas: primeiro o balconista conta a prateleira, depois repõe o
                    que o sistema mandou repor. O bloco termina quando as duas acabam.
                  </Ajuda>
                </h2>
                <div className="gr-etapa">
                  <span>
                    Contagem
                    <Ajuda rotulo="contagem">
                      Quantos produtos já foram contados na prateleira, de todos os blocos de hoje.
                    </Ajuda>
                  </span>
                  <b>{pct(d.progresso.contados, d.progresso.totalItens)}%</b>
                  <Barra valor={d.progresso.contados} total={d.progresso.totalItens} />
                  <em>
                    {d.progresso.contados} de {d.progresso.totalItens} SKUs
                  </em>
                </div>
                <div className="gr-etapa">
                  <span>
                    Reposição
                    <Ajuda rotulo="reposição">
                      Dos produtos que precisavam de reposição (prateleira abaixo do alvo e com
                      estoque), quantos já foram repostos.
                    </Ajuda>
                  </span>
                  <b>{pct(d.progresso.repostos, d.progresso.aRepor)}%</b>
                  <Barra valor={d.progresso.repostos} total={d.progresso.aRepor} tom="cinza" />
                  <em>
                    {d.progresso.repostos} de {d.progresso.aRepor} itens que precisam repor
                  </em>
                </div>
                <div className="gr-etapa">
                  <span>
                    Blocos concluídos
                    <Ajuda rotulo="blocos concluídos">
                      Blocos com contagem e reposição terminadas. Só bloco concluído inteiro conta a
                      vez no rodízio.
                    </Ajuda>
                  </span>
                  <b>{d.progresso.blocosConcluidos}</b>
                  <Barra
                    valor={d.progresso.blocosConcluidos}
                    total={
                      d.progresso.blocosConcluidos +
                      d.progresso.blocosEmAndamento +
                      d.progresso.blocosNaoIniciados
                    }
                    tom="ok"
                  />
                  <em>
                    {d.progresso.blocosConcluidos} concluídos · {d.progresso.blocosEmAndamento} em
                    andamento · {d.progresso.blocosNaoIniciados} não iniciado
                  </em>
                </div>
                <div className="gr-dupla">
                  <div>
                    <span>
                      Auditoria pendente
                      <Ajuda rotulo="auditoria pendente">
                        Blocos concluídos que ainda esperam o auditor conferir se a reposição foi
                        feita.
                      </Ajuda>
                    </span>
                    <strong>
                      {d.progresso.auditoriaPendente}{' '}
                      {d.progresso.auditoriaPendente === 1 ? 'bloco' : 'blocos'}
                    </strong>
                  </div>
                  <div>
                    <span>
                      Divergências
                      <Ajuda rotulo="divergências">
                        Itens em que o auditor achou diferente do que o balconista informou. Cada um
                        vira um caso em Divergências.
                      </Ajuda>
                    </span>
                    <strong className={d.progresso.divergencias ? 'gr-vermelho' : ''}>
                      {d.progresso.divergencias}
                    </strong>
                  </div>
                </div>
              </div>
              <div className="cv-cartao cv-sem-pad gr-setores">
                <div className="cv-cartao-topo cv-pad">
                  <h2>
                    Setores
                    <Ajuda rotulo="setores">
                      Como está cada setor hoje: quantos produtos, em quantos blocos foi dividido,
                      em que etapa estão os blocos, quanto já foi contado e quantas pessoas estão
                      presentes do total vinculado.
                    </Ajuda>
                  </h2>
                  <span className="cv-cinza cv-peq">
                    {d.setores.length} setores ·{' '}
                    {d.setores.reduce((a, s) => a + s.blocos.length, 0)} blocos · quantidade por
                    setor vem da configuração
                  </span>
                </div>
                <div className="cv-rolagem">
                  <table className="cv-tabela">
                    <thead>
                      <tr>
                        <th>Setor</th>
                        <th className="cv-dir">SKUs</th>
                        <th className="cv-dir">Blocos</th>
                        <th>Blocos por etapa</th>
                        <th>Contagem</th>
                        <th className="cv-dir">Presentes</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {d.setores.map((s) => {
                        const com = s.blocos.filter((b) => b.total > 0);
                        const cont = com.reduce((a, b) => a + b.contados, 0);
                        const tot = com.reduce((a, b) => a + (b.missaoId ? b.total : 0), 0);
                        const semEquipe = com.some((b) => b.etapa === 'AGUARDANDO_RESPONSAVEL');
                        const conta = (e: EtapaDoBloco[]) =>
                          com.filter((b) => e.includes(b.etapa)).length;
                        return (
                          <tr key={s.setorId}>
                            <td>
                              <div className="cv-forte">{s.nome}</div>
                              {(semEquipe || !s.geracao || s.descricao) && (
                                <div className={`cv-peq ${semEquipe ? 'gr-vermelho' : 'cv-cinza'}`}>
                                  {semEquipe
                                    ? 'sem responsável disponível'
                                    : !s.geracao
                                      ? 'sem operação hoje'
                                      : s.descricao}
                                </div>
                              )}
                            </td>
                            <td className="cv-dir">{s.geracao?.elegiveis ?? '—'}</td>
                            <td className="cv-dir cv-forte">{s.blocos.length || '—'}</td>
                            <td>
                              <span className="gr-chips-etapa">
                                {conta(['CONCLUIDO', 'AUDITORIA_PENDENTE']) > 0 && (
                                  <span className="gr-chip gr-chip-ok">
                                    {conta(['CONCLUIDO', 'AUDITORIA_PENDENTE'])} concluído
                                  </span>
                                )}
                                {conta(['CONTAGEM', 'REPOSICAO']) > 0 && (
                                  <span className="gr-chip gr-chip-and">
                                    {conta(['CONTAGEM', 'REPOSICAO'])} em andamento
                                  </span>
                                )}
                                {conta(['NAO_INICIADO', 'AGUARDANDO_RESPONSAVEL', 'RASCUNHO']) >
                                  0 && (
                                  <span className="gr-chip">
                                    {conta(['NAO_INICIADO', 'AGUARDANDO_RESPONSAVEL', 'RASCUNHO'])}{' '}
                                    não iniciado
                                  </span>
                                )}
                              </span>
                            </td>
                            <td>
                              <div className="gr-cont">
                                <Barra
                                  valor={cont}
                                  total={tot}
                                  tom={cont >= tot && tot > 0 ? 'ok' : undefined}
                                />
                                <b>{tot ? `${String(pct(cont, tot))}%` : '—'}</b>
                              </div>
                            </td>
                            <td className={`cv-dir ${semEquipe ? 'gr-vermelho cv-forte' : ''}`}>
                              {s.presentesHoje} / {s.vinculados}
                            </td>
                            <td className="cv-dir">
                              <a className="gr-link" href={`#/giro-operacao?setor=${s.setorId}`}>
                                Abrir setor
                              </a>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <Alertas d={d} />
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function Alertas({ d }: { d: OperacaoDoDia }) {
  return (
    <div className="cv-cartao cv-sem-pad gr-alertas">
      <div className="cv-cartao-topo cv-pad">
        <h2>
          Alertas operacionais <span className="cv-bola">{d.alertas.length}</span>
          <Ajuda rotulo="alertas operacionais">
            O que precisa de ação agora: bloco sem responsável, responsável ausente, estoque sem
            leitura. Cada alerta tem o atalho para resolver.
          </Ajuda>
        </h2>
      </div>
      {d.alertas.length === 0 ? (
        <p className="cv-vazio">Nenhum alerta na operação de hoje.</p>
      ) : (
        <ul>
          {d.alertas.map((a, k) => (
            <li key={k}>
              <Icone
                nome={
                  a.tipo === 'SEM_RESPONSAVEL'
                    ? 'users'
                    : a.tipo === 'RESPONSAVEL_AUSENTE'
                      ? 'clock'
                      : a.tipo === 'ESTOQUE_INDISPONIVEL'
                        ? 'box'
                        : 'filter'
                }
                tamanho={15}
                traco={1.7}
              />
              <div>
                <strong>{a.titulo}</strong>
                <span>{a.detalhe}</span>
                {a.acao && (
                  <a
                    href={
                      a.tipo === 'SEM_RESPONSAVEL'
                        ? `#/giro-equipe${a.setorId ? `?setor=${a.setorId}` : ''}`
                        : `#/giro-operacao${a.setorId ? `?setor=${a.setorId}` : ''}`
                    }
                  >
                    {a.acao}
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SemOperacao({
  dia,
  vendasDe,
  semSetores,
}: {
  dia: string;
  vendasDe: string;
  semSetores: boolean;
}) {
  return (
    <div className="cv-cartao gr-vazio-grande">
      {semSetores ? (
        <>
          <h2>Nenhum setor configurado</h2>
          <p>
            Crie os setores e diga quais grupos do catálogo moram em cada um. A operação do dia
            nasce das vendas de ontem.
          </p>
          <a className="cv-botao-primario gr-botao-link" href="#/giro-setores">
            Configurar setores
          </a>
        </>
      ) : (
        <>
          <h2>Nenhuma operação gerada para {diaCurto(dia)}</h2>
          <p>A missão do dia é criada a partir das vendas de {vendasDe}.</p>
          <a className="cv-botao-primario gr-botao-link" href="#/giro-geracao">
            Simular geração
          </a>
        </>
      )}
    </div>
  );
}

/* ================================================================== *
 * 16B — Operação de hoje (mapa de blocos)
 * ================================================================== */

export function OperacaoDeHoje({
  sessao,
  parametros,
}: {
  sessao: Sessao;
  parametros: URLSearchParams;
}) {
  const hoje = hojeBrasilia();
  // ?dia= abre a operação de outro dia (o Histórico leva para cá), com a foto daquele dia
  const pedido = parametros.get('dia');
  const dia = pedido && /^\d{4}-\d{2}-\d{2}$/.test(pedido) && pedido <= hoje ? pedido : hoje;
  const doHistorico = dia !== hoje;
  const { d, erro, carregar } = useOperacao(dia);
  const [setorSel, setSetorSel] = useState<string | null>(parametros.get('setor'));
  // Aba Fotos (Carlos, 02/10): escolhe a pessoa e vê as fotos da caixa e da prateleira.
  const [vista, setVista] = useState<'blocos' | 'fotos'>(
    parametros.get('vista') === 'fotos' ? 'fotos' : 'blocos',
  );
  const [transferindo, setTransferindo] = useState<{
    setor: SetorDaOperacao;
    bloco: BlocoDaOperacao;
  } | null>(null);
  const setor =
    d?.setores.find((s) => s.setorId === setorSel) ??
    d?.setores.find((s) => s.geracao) ??
    d?.setores[0];
  const pub = setor?.geracao;
  return (
    <section className="cv gr">
      <SubnavDoGiro
        atual="giro-operacao"
        excecoes={d?.foraTotal}
        direita={
          pub?.publicadoEm
            ? `publicada ${hora(pub.publicadoEm)} · atualizado ${d ? hora(d.atualizadoEm) : ''}`
            : undefined
        }
      />
      {doHistorico && (
        <div className="gr-historico-aviso" role="status">
          Operação de <strong>{diaCurto(dia)}</strong> — como ficou naquele dia.
          <a href="#/giro-operacao">Voltar para hoje</a>
        </div>
      )}
      <div className="gr-op-abas" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={vista === 'blocos'}
          className={vista === 'blocos' ? 'gr-op-aba-sel' : ''}
          onClick={() => setVista('blocos')}
        >
          Blocos
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={vista === 'fotos'}
          className={vista === 'fotos' ? 'gr-op-aba-sel' : ''}
          onClick={() => setVista('fotos')}
        >
          <Icone nome="cam" tamanho={14} traco={1.7} /> Fotos
        </button>
      </div>
      {vista === 'fotos' ? (
        <FotosDoGiro dia={dia} />
      ) : erro ? (
        <div className="cv-corpo">
          <ErroDeArea oQue="a operação" aoTentar={carregar} />
        </div>
      ) : !d ? (
        <div className="cv-corpo">
          <div className="cv-carregando" aria-busy="true" />
        </div>
      ) : d.setores.every((s) => !s.geracao) ? (
        <div className="cv-corpo">
          <SemOperacao dia={dia} vendasDe={vendasUsadas(d)} semSetores={!d.setores.length} />
        </div>
      ) : (
        <div className="gr-op">
          <aside className="gr-op-setores">
            <span className="gr-rotulo">
              SETORES
              <Ajuda rotulo="setores">
                Toque num setor para ver os blocos dele. A barra mostra quanto do setor já foi
                contado hoje.
              </Ajuda>
            </span>
            {d.setores.map((s) => {
              const com = s.blocos.filter((b) => b.total > 0);
              const cont = com.reduce((a, b) => a + b.contados, 0);
              const tot = com.reduce((a, b) => a + (b.missaoId ? b.total : 0), 0);
              const semEquipe = com.some((b) => b.etapa === 'AGUARDANDO_RESPONSAVEL');
              const todos =
                tot > 0 && com.every((b) => ['CONCLUIDO', 'AUDITORIA_PENDENTE'].includes(b.etapa));
              return (
                <button
                  key={s.setorId}
                  type="button"
                  className={`gr-op-setor ${setor?.setorId === s.setorId ? 'gr-op-setor-sel' : ''} ${semEquipe ? 'gr-op-setor-alerta' : ''}`}
                  onClick={() => setSetorSel(s.setorId)}
                >
                  <span className="gr-op-setor-topo">
                    <b>{s.nome}</b>
                    <em>{s.blocos.length}</em>
                  </span>
                  <Barra
                    valor={cont}
                    total={tot || 1}
                    tom={todos ? 'ok' : semEquipe ? 'cinza' : undefined}
                  />
                  <span className="gr-op-setor-rod">
                    {!s.geracao
                      ? 'sem operação'
                      : semEquipe
                        ? 'sem equipe presente'
                        : `${String(s.geracao.elegiveis)} SKUs · ${todos ? 'concluído' : `${String(pct(cont, tot))}%`}`}
                  </span>
                </button>
              );
            })}
            <p className="gr-op-nota">
              <b>{d.setores.reduce((a, s) => a + s.blocos.length, 0)} blocos</b> nesta operação. A
              quantidade por setor vem da configuração vigente em {diaCurto(dia)}.
            </p>
          </aside>
          {setor && (
            <div className="gr-op-corpo">
              <div className="gr-op-cab">
                <h1>{setor.nome}</h1>
                <span>
                  {setor.geracao
                    ? `${String(setor.geracao.elegiveis)} SKUs · ${String(setor.blocos.length)} blocos · carga média ${String(setor.geracao.cargaAlvo).replace('.', ',')}`
                    : 'sem operação hoje'}
                  <em> (média, não obrigação)</em>
                  <Ajuda rotulo="os blocos do setor">
                    Cada bloco é uma parte do setor (por letra ou por laboratório) entregue a uma
                    pessoa. Carga média = quantos produtos cada bloco tem em média hoje. Toque num
                    bloco para ver os produtos; &quot;Trocar pessoa&quot; passa o bloco para outra
                    pessoa antes de ela começar.
                  </Ajuda>
                </span>
                {setor.geracao?.estado === 'RASCUNHO' && (
                  <span className="gr-selo gr-selo-RASCUNHO">Rascunho — ainda não publicada</span>
                )}
                {setor.geracao?.estado === 'CANCELADA' && (
                  <span className="gr-selo gr-selo-CANCELADO">
                    Cancelada: {setor.geracao.motivoCancelamento}
                  </span>
                )}
              </div>
              <div className="gr-blocos">
                {setor.blocos.map((b) => (
                  <CartaoDoBloco
                    key={b.numero}
                    b={b}
                    geracaoId={setor.geracao?.id ?? ''}
                    cargaAlvo={setor.geracao?.cargaAlvo ?? 0}
                    aoTransferir={() => setTransferindo({ setor, bloco: b })}
                  />
                ))}
              </div>
              {setor.blocos.length > 0 && (
                <p className="gr-op-rodape">
                  <Icone nome="filter" tamanho={14} traco={1.6} />
                  {bN(1)}…{bN(setor.blocos.length)} são fixos neste setor. As faixas{' '}
                  {setor.blocos
                    .filter((b) => b.total)
                    .slice(0, 2)
                    .map((b) => b.rotulo)
                    .join(', ')}
                  … valem só para a operação de {diaCurto(dia)} — na próxima geração podem mudar.
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {transferindo && (
        <Transferencia
          sessao={sessao}
          setor={transferindo.setor}
          bloco={transferindo.bloco}
          dia={dia}
          aoFechar={() => setTransferindo(null)}
          aoConcluir={() => {
            setTransferindo(null);
            carregar();
          }}
        />
      )}
    </section>
  );
}

function CartaoDoBloco({
  b,
  geracaoId,
  cargaAlvo,
  aoTransferir,
}: {
  b: BlocoDaOperacao;
  geracaoId: string;
  cargaAlvo: number;
  aoTransferir: () => void;
}) {
  const etapa: EtapaDoBloco =
    b.transferencia && !['CONCLUIDO', 'AUDITORIA_PENDENTE', 'PARCIAL'].includes(b.etapa)
      ? 'CONTAGEM'
      : b.etapa;
  const ausente = b.responsavel?.presenca === 'AUSENTE' && b.etapa === 'NAO_INICIADO';
  const fase = ['REPOSICAO', 'CONCLUIDO', 'AUDITORIA_PENDENTE'].includes(b.etapa) && b.aRepor > 0;
  const acima = cargaAlvo > 0 && b.total > cargaAlvo * 1.3;
  return (
    <a
      className={`gr-bloco ${ausente ? 'gr-bloco-alerta' : ''} ${b.total === 0 ? 'gr-bloco-vazio' : ''}`}
      href={b.missaoId ? `#/giro-bloco?g=${geracaoId}&n=${String(b.numero)}` : undefined}
    >
      <div className="gr-bloco-topo">
        <b>{bN(b.numero)}</b>
        <span className="gr-faixa">{b.rotulo}</span>
        <span style={{ flex: 1 }} />
        {b.transferencia ? (
          <span className="gr-selo gr-selo-transf">Transferido</span>
        ) : (
          <Selo etapa={ausente ? 'NAO_INICIADO' : etapa} />
        )}
      </div>
      <span className="gr-bloco-skus">
        {b.total} SKUs{acima ? ' · acima da média' : ''}
      </span>
      {b.responsavel ? (
        <div className="gr-pessoa">
          <span className={`gr-avatar ${ausente ? 'gr-avatar-fora' : ''}`}>
            {iniciais(b.responsavel.nome)}
          </span>
          <div>
            <b className={ausente ? 'cv-cinza' : ''}>{b.responsavel.nome}</b>
            <em className={ausente ? 'gr-vermelho' : ''}>
              {ausente
                ? 'ausente hoje · rodízio não consumido'
                : b.transferencia
                  ? `recebeu ${String(b.total - b.transferencia.contadosAntes)} pendentes de ${primeiroNome(b.transferencia.deNome)}`
                  : b.concluidaEm && b.iniciadaEm
                    ? `${hora(b.iniciadaEm)} → ${hora(b.concluidaEm)}`
                    : b.contagemConcluidaEm
                      ? `contagem fechada ${hora(b.contagemConcluidaEm)}`
                      : b.iniciadaEm
                        ? `início ${hora(b.iniciadaEm)}`
                        : 'ainda não iniciou'}
            </em>
          </div>
        </div>
      ) : b.total > 0 ? (
        <div className="gr-pessoa">
          <span className="gr-avatar gr-avatar-fora">—</span>
          <div>
            <b className="cv-cinza">Sem responsável</b>
            <em className="gr-vermelho">ninguém presente para este bloco</em>
          </div>
        </div>
      ) : (
        <p className="cv-cinza cv-peq">Sem carga hoje — o bloco continua existindo.</p>
      )}
      <span style={{ flex: 1 }} />
      {ausente || (b.etapa === 'AGUARDANDO_RESPONSAVEL' && b.total > 0) ? (
        podeTransferirBloco(b) ? (
          <button
            type="button"
            className="cv-botao-primario gr-transferir"
            onClick={(e) => {
              e.preventDefault();
              aoTransferir();
            }}
          >
            <Icone nome="cycle" tamanho={14} traco={1.8} /> Transferir bloco
          </button>
        ) : null
      ) : b.total > 0 ? (
        <div className="gr-bloco-rod">
          <div className="gr-bloco-rod-l">
            <span>
              {b.transferencia
                ? 'Contagem'
                : b.etapa === 'AUDITORIA_PENDENTE'
                  ? 'Aguarda auditoria'
                  : fase
                    ? b.etapa === 'REPOSICAO'
                      ? 'Reposição'
                      : 'Contagem · reposição'
                    : 'Contagem'}
            </span>
            <b>
              {b.transferencia
                ? `${String(b.transferencia.contadosAntes)} + ${String(b.contadosPorResponsavel)} / ${String(b.total)}`
                : b.etapa === 'REPOSICAO'
                  ? `${String(b.repostos)} / ${String(b.aRepor)}`
                  : `${String(b.contados)} / ${String(b.total)}`}
            </b>
          </div>
          <Barra
            valor={b.etapa === 'REPOSICAO' ? b.repostos : b.contados}
            total={b.etapa === 'REPOSICAO' ? b.aRepor : b.total}
            tom={
              ['CONCLUIDO', 'AUDITORIA_PENDENTE'].includes(b.etapa)
                ? 'ok'
                : b.etapa === 'REPOSICAO'
                  ? 'rep'
                  : b.transferencia
                    ? 'cinza'
                    : undefined
            }
          />
          {(b.repostos > 0 || b.divergencias > 0 || b.transferencia || b.fotos > 0) && (
            <em>
              {b.transferencia
                ? `${String(b.transferencia.contadosAntes)} de ${primeiroNome(b.transferencia.deNome)} · ${String(b.contadosPorResponsavel)} de ${primeiroNome(b.responsavel?.nome)}`
                : [
                    b.repostos ? `${String(b.repostos)} itens repostos` : '',
                    b.divergencias
                      ? `${String(b.divergencias)} divergência${b.divergencias > 1 ? 's' : ''}`
                      : '',
                    b.fotos ? `${String(b.fotos)} fotos de auditoria` : '',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
            </em>
          )}
          {/* Blocos fixos, balconistas trocam (dono, 27/09/2026): só bloco NÃO COMEÇADO muda de
              pessoa — começou, não transfere. */}
          {b.etapa === 'NAO_INICIADO' && podeTransferirBloco(b) && (
            <button
              type="button"
              className="cv-botao gr-transferir gr-transferir-sec"
              onClick={(e) => {
                e.preventDefault();
                aoTransferir();
              }}
            >
              <Icone nome="cycle" tamanho={13} traco={1.8} /> Trocar pessoa
            </button>
          )}
        </div>
      ) : null}
    </a>
  );
}

export function Transferencia({
  sessao,
  setor,
  bloco,
  dia,
  paraInicial = '',
  aoFechar,
  aoConcluir,
}: {
  sessao: Sessao;
  setor: Pick<SetorDaOperacao, 'setorId' | 'nome' | 'blocos'>;
  bloco: BlocoDaOperacao;
  dia: string;
  /** Quem já vem escolhido (arrastado no quadro de blocos). */
  paraInicial?: string;
  aoFechar: () => void;
  aoConcluir: () => void;
}) {
  const [pessoas, setPessoas] = useState<readonly PessoaDaEquipe[] | null>(null);
  const [para, setPara] = useState(paraInicial);
  const [motivo, setMotivo] = useState('');
  const [falha, setFalha] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => {
    void lerEquipe(dia, setor.setorId).then((r) => {
      if (r.ok)
        setPessoas(
          r.dados.pessoas.filter(
            (p) =>
              p.presenca !== 'AUSENTE' &&
              p.accountId !== bloco.responsavel?.accountId &&
              // quem já começou o bloco dela não troca (dono, 27/09/2026)
              blocoDaTroca(bloco, p.accountId, setor.blocos) !== null,
          ),
        );
    });
  }, [dia, setor.setorId, setor.blocos, bloco]);
  const troca = para ? blocoDaTroca(bloco, para, setor.blocos) : undefined;
  const confirmar = () => {
    if (!para) return setFalha('Escolha quem recebe.');
    if (motivo.trim().length < 5) return setFalha('Escreva o motivo da transferência.');
    setEnviando(true);
    setFalha(null);
    void transferir(sessao, bloco.missaoId ?? '', para, motivo.trim()).then((r) => {
      setEnviando(false);
      if (r.tipo === 'confirmada') aoConcluir();
      else if (r.tipo === 'recusada') setFalha(r.mensagem);
      else
        setFalha(
          'Sem resposta do servidor: a transferência pode ter sido feita. Recarregue antes de repetir.',
        );
    });
  };
  const destino = pessoas?.find((p) => p.accountId === para);
  return (
    <div className="cv-veu" onClick={aoFechar}>
      <div
        className="gr-modal"
        role="dialog"
        aria-label={troca ? 'Trocar balconistas de bloco' : 'Transferir bloco'}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>{troca ? 'Trocar balconistas de bloco' : 'Transferir bloco'}</h2>
        <p className="cv-cinza cv-peq">
          {setor.nome} · {bN(bloco.numero)} · {bloco.rotulo} · operação de {diaCurto(dia)}
        </p>
        <div className="gr-de-para">
          <div>
            <span>DE</span>
            <b>{bloco.responsavel?.nome ?? '—'}</b>
            <em>
              {bN(bloco.numero)} · {bloco.total} itens · não começou
            </em>
          </div>
          <Icone nome="cycle" tamanho={16} traco={1.7} />
          <div>
            <span>PARA</span>
            <select value={para} onChange={(e) => setPara(e.target.value)} aria-label="Quem recebe">
              <option value="">Escolha…</option>
              {pessoas?.map((p) => (
                <option key={p.accountId} value={p.accountId}>
                  {p.nome}
                </option>
              ))}
            </select>
            <em>
              {destino
                ? troca
                  ? `presente · hoje no ${bN(troca.numero)} (não começou)`
                  : 'presente · sem bloco neste setor hoje'
                : ' '}
            </em>
          </div>
        </div>
        <div className="gr-muda">
          <span className="gr-rotulo">O QUE MUDA</span>
          <p>
            <Icone nome="check" tamanho={13} traco={2} /> <b>{bN(bloco.numero)}</b> passa para{' '}
            {destino ? primeiroNome(destino.nome) : 'quem recebe'}
          </p>
          {troca && (
            <p>
              <Icone nome="cycle" tamanho={13} traco={2} /> <b>{bN(troca.numero)}</b> passa para{' '}
              {primeiroNome(bloco.responsavel?.nome)} — os dois TROCAM
            </p>
          )}
          <p>
            <Icone nome="x" tamanho={13} traco={2} /> Os blocos são fixos: só a pessoa muda, e só
            enquanto o bloco não começou
          </p>
        </div>
        <label className="gr-rotulo" htmlFor="gr-motivo">
          MOTIVO
        </label>
        <textarea
          id="gr-motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={3}
          placeholder="Ex.: remanejada para o caixa…"
          maxLength={500}
        />
        {falha && (
          <p className="cv-falha" role="alert">
            {falha}
          </p>
        )}
        <div className="gr-modal-acoes">
          <button
            type="button"
            className="cv-botao-primario"
            disabled={enviando}
            onClick={confirmar}
          >
            {troca ? 'Confirmar troca' : 'Confirmar transferência'}
          </button>
          <button type="button" className="cv-botao" onClick={aoFechar}>
            Cancelar
          </button>
        </div>
        <p className="cv-cinza cv-peq">
          Registrado com autor, horário e motivo. O histórico do bloco mostra os dois trechos.
        </p>
      </div>
    </div>
  );
}

/* ================================================================== *
 * 16C — Detalhe do bloco
 * ================================================================== */

const ETAPAS = ['Pronto', 'Contagem', 'Reposição', 'Auditoria', 'Concluído'] as const;
function passoAtual(e: EtapaDoBloco): number {
  if (e === 'CONTAGEM') return 1;
  if (e === 'REPOSICAO') return 2;
  if (e === 'AUDITORIA_PENDENTE') return 3;
  if (e === 'CONCLUIDO' || e === 'PARCIAL') return 4;
  return 0;
}
const STATUS_DO_ITEM: Record<string, { rotulo: string; tom: string }> = {
  PENDING_SCAN: { rotulo: 'Pendente', tom: '' },
  PENDING_COUNT: { rotulo: 'Pendente', tom: '' },
  WAITING_STOCK: { rotulo: 'Problema de dados', tom: 'gr-st-dados' },
  COUNTED_NO_REPLENISHMENT: { rotulo: 'Contado', tom: 'gr-st-ok' },
  COUNTED_REPLENISHMENT_REQUIRED: { rotulo: 'Contado', tom: 'gr-st-ok' },
  PENDING_REPLENISHMENT: { rotulo: 'A repor', tom: 'gr-st-rep' },
  REPLENISHED_OK: { rotulo: 'Reposto', tom: 'gr-st-ok' },
  DIVERGENCE_OPEN: { rotulo: 'Divergência', tom: 'gr-st-div' },
  AUDIT_PENDING: { rotulo: 'Divergência', tom: 'gr-st-div' },
  DONE: { rotulo: 'Contado', tom: 'gr-st-ok' },
  TRANSFERRED: { rotulo: 'Transferido', tom: '' },
  CANCELLED: { rotulo: 'Cancelado', tom: '' },
};

export function DetalheDoBlocoDoGiro({ geracaoId, numero }: { geracaoId: string; numero: number }) {
  const [d, setD] = useState<DetalheDoBloco | null>(null);
  const [erro, setErro] = useState(false);
  const [busca, setBusca] = useState('');
  const carregar = useCallback(() => {
    setErro(false);
    void lerBloco(geracaoId, numero).then((r) => {
      if (r.ok) setD(r.dados);
      else setErro(true);
    });
  }, [geracaoId, numero]);
  useEffect(carregar, [carregar]);
  const b = d?.bloco;
  const passo = b ? passoAtual(b.etapa) : 0;
  const lista =
    d?.produtos.filter(
      (p) =>
        !busca ||
        p.nome.toLowerCase().includes(busca.toLowerCase()) ||
        (p.codigo ?? '').includes(busca),
    ) ?? [];
  return (
    <section className="cv gr">
      <SubnavDoGiro atual="giro-operacao" />
      <div className="cv-corpo gr-detalhe">
        <a href="#/giro-operacao" className="cv-voltar">
          <Icone nome="arrow-l" tamanho={14} traco={1.8} />{' '}
          {d ? `${d.setor} · operação de ${diaCurto(d.dia)}` : 'Operação de hoje'}
        </a>
        {erro && <ErroDeArea oQue="o bloco" aoTentar={carregar} />}
        {d && b && (
          <div className="cv-cartao cv-sem-pad">
            <div className="gr-det-cab">
              <h1>
                {bloco2(b.numero)} <span className="gr-faixa">{b.rotulo}</span>
                {b.transferencia ? (
                  <span className="gr-selo gr-selo-transf">Transferido</span>
                ) : (
                  <Selo etapa={b.etapa} />
                )}
              </h1>
              <p>
                {b.total} SKUs · faixa calculada nesta geração
                {b.responsavel ? ` · ${b.responsavel.nome}` : ''}
              </p>
            </div>
            <ol className="gr-passos">
              {ETAPAS.map((e, i) => (
                <li
                  key={e}
                  className={i < passo ? 'gr-passo-feito' : i === passo ? 'gr-passo-atual' : ''}
                >
                  <span>{i < passo ? <Icone nome="check" tamanho={12} traco={2.2} /> : i + 1}</span>
                  {e}
                </li>
              ))}
            </ol>
            <div className="cv-cupom-faixa">
              <div>
                <span>CONTADOS</span>
                <strong>
                  {b.contados} <em className="cv-cinza gr-de">de {b.total}</em>
                </strong>
              </div>
              <div>
                <span>A REPOR</span>
                <strong>{b.aRepor}</strong>
              </div>
              <div>
                <span>DIVERGÊNCIAS</span>
                <strong className={b.divergencias ? 'gr-vermelho' : ''}>{b.divergencias}</strong>
              </div>
              <div>
                <span>{d.pausadoMin > 0 ? 'TEMPO ATIVO' : 'DURAÇÃO'}</span>
                <strong>{d.ativoMin === null ? '—' : horasMin(d.ativoMin)}</strong>
                {(d.pausadoMin > 0 || d.emPausa) && (
                  <em className="cv-cinza cv-peq" style={{ fontStyle: 'normal' }}>
                    {d.emPausa ? 'em pausa agora · ' : ''}
                    {horasMin(d.pausadoMin)} em pausa
                  </em>
                )}
              </div>
            </div>
            <div className="cv-pad">
              {b.transferencia && (
                <div className="gr-aviso-azul">
                  <Icone nome="cycle" tamanho={15} traco={1.7} />
                  <p>
                    <b>
                      Transferido às {hora(b.transferencia.em)}
                      {d.eventos.find((e) => e.tipo === 'transferencia')?.quem
                        ? ` por ${String(d.eventos.find((e) => e.tipo === 'transferencia')?.quem)}`
                        : ''}
                      .
                    </b>{' '}
                    Motivo: &quot;{b.transferencia.motivo}&quot;. Os {b.transferencia.contadosAntes}{' '}
                    itens já contados seguem atribuídos a {b.transferencia.deNome};{' '}
                    {b.responsavel?.nome} assumiu os pendentes. O bloco não conta como concluído por
                    nenhum dos dois.
                  </p>
                </div>
              )}
              <div className="cv-cartao-topo gr-det-prod-topo">
                <h2>Produtos do bloco</h2>
                <label className="cv-busca gr-busca-peq">
                  <Icone nome="search" tamanho={14} traco={1.7} />
                  <input
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Produto ou código"
                    aria-label="Buscar no bloco"
                  />
                </label>
              </div>
            </div>
            <div className="cv-rolagem">
              <table className="cv-tabela">
                <thead>
                  <tr>
                    <th>L</th>
                    <th>Produto</th>
                    <th className="cv-dir" title="Vendido na janela da captura (ontem, por padrão)">
                      Vendeu
                    </th>
                    <th className="cv-dir" title="Média de venda por dia nos últimos 30 dias">
                      Média/dia
                    </th>
                    <th className="cv-dir">Prateleira</th>
                    <th className="cv-dir">Alvo</th>
                    <th className="cv-dir">Estoque</th>
                    <th className="cv-dir">Repor</th>
                    <th>Status · autor</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((p) => {
                    const st = p.naoLocalizado
                      ? { rotulo: 'Não localizado', tom: 'gr-st-rep' }
                      : (STATUS_DO_ITEM[p.estado] ?? { rotulo: p.estado, tom: '' });
                    return (
                      <tr key={p.itemId}>
                        <td className="cv-cinza cv-mono">{p.letra}</td>
                        <td>
                          <div className="cv-forte">{p.nome}</div>
                          <div className="cv-mono cv-cinza cv-peq">
                            {p.codigo ?? '—'}
                            {p.fabricante && ` · ${p.fabricante}`}
                            {p.origem === 'NAO_VENDIDO' && ' · não vendido'}
                          </div>
                        </td>
                        <td className="cv-dir">{p.qtdVendida}</td>
                        <td className="cv-dir">
                          {p.mediaDiaria === null
                            ? '—'
                            : p.mediaDiaria.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
                        </td>
                        <td className="cv-dir">{p.prateleira ?? '—'}</td>
                        <td className="cv-dir">{p.alvo}</td>
                        <td className={`cv-dir ${p.estoque === null ? 'gr-indisp' : ''}`}>
                          {p.prateleira === null && p.estoque !== null
                            ? p.estoqueTexto
                            : p.estoqueTexto}
                        </td>
                        <td className="cv-dir cv-forte">{p.repor ?? '—'}</td>
                        <td>
                          <span className={`gr-st ${st.tom}`}>{st.rotulo}</span>{' '}
                          <span className="cv-cinza cv-peq">{p.autor ?? ''}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="cv-rodape">
              <span>
                <Icone nome="cam" tamanho={13} traco={1.6} /> {b.fotos} foto(s) de evidência neste
                bloco
              </span>
              <span>
                Estoque só aparece aqui para o administrador — para o balconista, depois da contagem
              </span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/** 65 → "1h05"; 20 → "20 min". */
function horasMin(min: number) {
  return min < 60
    ? `${String(min)} min`
    : `${String(Math.floor(min / 60))}h${String(min % 60).padStart(2, '0')}`;
}
